import {
  FormGroup,
  getDisplayNameOrNameLabel,
  HStack,
  LoaderTalos,
  MixpanelEventSource,
  MixpanelSourceProvider,
  SearchSelect,
  Text,
  VStack,
  type Customer,
  type MarketAccount,
  type ProductTypeEnum,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useCustomers } from '../../../../hooks/useCustomer';
import { useCustomerMarketAccounts } from '../../../../providers/CustomerMarketAccountsProvider';
import type { CustomerPriceGridV2PanelConfig } from '../../../../types/LayoutConfig';
import { getIsBBOEnabled } from '../../bbo';
import { expandSecurityToPriceGridRows } from '../../expandSecurityToPriceGridRows';
import { usePriceGridDataObservable, usePriceGridFilter } from '../../hooks';
import { usePriceGridSettings } from '../../hooks/usePriceGridSettings';
import { PriceGrid } from '../../PriceGrid';
import { PriceGridBody } from '../../PriceGridHeaderLayout';
import { PriceGridSettingsContext } from '../../PriceGridSettingsContext';
import { getShowRFQ } from '../../rfqVisibility';
import type { PriceGridFilterConfig, PriceGridSourceItem } from '../../types';
import { getCustomerPriceGridColumns } from './columns';
import { CustomerPriceGridHeader } from './CustomerPriceGridHeader';
import { CUSTOMER_PRICE_GRID_FILTER_CONFIG } from './filters';
import { useCustomerPriceGridSource } from './useCustomerPriceGridSource';

export interface CustomerPriceGridProps {
  blotterID: string;
  customerID?: string;
  marketAccount?: string;
  filterConfig?: PriceGridFilterConfig<PriceGridSourceItem>;
  filterState?: CustomerPriceGridV2PanelConfig['filterState'];
  onParametersChange?: (
    parameters: Partial<Pick<CustomerPriceGridV2PanelConfig, 'customerID' | 'filterState' | 'marketAccount'>>
  ) => void;
}

export function CustomerPriceGrid({
  blotterID,
  customerID,
  marketAccount,
  filterConfig = CUSTOMER_PRICE_GRID_FILTER_CONFIG,
  filterState: persistedFilterState,
  onParametersChange,
}: CustomerPriceGridProps) {
  const customers = useCustomers();
  const { getActiveCustomerMarketAccounts } = useCustomerMarketAccounts();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [selectedMarketAccount, setSelectedMarketAccount] = useState<MarketAccount | undefined>();

  const resolvedCustomer = useMemo(() => {
    const customerIDNumber = customerID == null ? undefined : Number(customerID);
    return selectedCustomer ?? customers?.find(customer => customer.CounterpartyID === customerIDNumber);
  }, [customerID, customers, selectedCustomer]);

  const availableMarketAccounts = useMemo(
    () => (resolvedCustomer ? getActiveCustomerMarketAccounts(resolvedCustomer.Name) : []),
    [getActiveCustomerMarketAccounts, resolvedCustomer]
  );
  const resolvedMarketAccount = useMemo(() => {
    const persistedMarketAccount = marketAccount
      ? availableMarketAccounts.find(availableMarketAccount => availableMarketAccount.Name === marketAccount)
      : undefined;
    const marketAccountSelection = selectedMarketAccount ?? persistedMarketAccount;
    if (!resolvedCustomer || marketAccountSelection?.Counterparty !== resolvedCustomer.Name) {
      return undefined;
    }

    return marketAccountSelection;
  }, [availableMarketAccounts, marketAccount, resolvedCustomer, selectedMarketAccount]);

  const handleCustomerChanged = useCallback(
    (customer: Customer | undefined) => {
      const nextMarketAccounts = customer ? getActiveCustomerMarketAccounts(customer.Name) : [];
      const nextMarketAccount = nextMarketAccounts.length === 1 ? nextMarketAccounts[0] : undefined;

      setSelectedCustomer(customer);
      setSelectedMarketAccount(nextMarketAccount);
      onParametersChange?.({
        customerID: customer?.CounterpartyID == null ? undefined : String(customer.CounterpartyID),
        marketAccount: nextMarketAccount?.Name,
      });
    },
    [getActiveCustomerMarketAccounts, onParametersChange]
  );
  const handleMarketAccountChanged = useCallback(
    (marketAccount: MarketAccount | undefined) => {
      setSelectedMarketAccount(marketAccount);
      onParametersChange?.({
        customerID: resolvedCustomer?.CounterpartyID == null ? undefined : String(resolvedCustomer.CounterpartyID),
        marketAccount: marketAccount?.Name,
      });
    },
    [onParametersChange, resolvedCustomer]
  );

  const filtersPortalId = `${blotterID}-customer-price-grid-filters`;
  const { sourceItems, sourceObservable } = useCustomerPriceGridSource(resolvedCustomer);
  const { filterState, setFilterState, setSearch, filterSourceItems } = usePriceGridFilter({
    filterConfig,
    filterState: persistedFilterState,
    onFilterStateChanged: nextFilterState => onParametersChange?.({ filterState: nextFilterState }),
  });
  const availableProductTypes = useMemo(
    () =>
      [...new Set(sourceItems.map(sourceItem => sourceItem.security.ProductType))].filter(
        (productType): productType is ProductTypeEnum => productType != null
      ),
    [sourceItems]
  );
  const dataObservable = usePriceGridDataObservable({
    sourceObservable,
    filterSourceItems,
    expandRows: expandSecurityToPriceGridRows,
  });
  const isBBOEnabled = getIsBBOEnabled(filterState);
  const showRFQ = getShowRFQ(filterState);
  const columns = useMemo(
    () => getCustomerPriceGridColumns({ counterparty: resolvedCustomer?.Name, isBBOEnabled, showRFQ }),
    [isBBOEnabled, resolvedCustomer?.Name, showRFQ]
  );

  const priceGridSettings = usePriceGridSettings(filterState, setFilterState, MixpanelEventSource.CustomerPriceGrid);

  const isResolvingPersistedCustomer = customerID != null && !selectedCustomer && !resolvedCustomer;
  const showInitialState = isResolvingPersistedCustomer || !resolvedCustomer;

  const initialState = useMemo(
    () =>
      isResolvingPersistedCustomer ? (
        <VStack h="100%" w="100%" justifyContent="center" alignItems="center">
          <LoaderTalos />
        </VStack>
      ) : (
        <VStack h="100%" w="100%" justifyContent="center" alignItems="center" gap="spacing12" p="spacing24">
          <Text color="colorTextImportant" fontWeight={600}>
            No customer selected
          </Text>
          <Text color="colorTextSecondary" textAlign="center" maxWidth="25rem">
            Choose a customer to view customer-specific prices next to aggregate market prices.
          </Text>
          <HStack gap="spacing8" w="12.5rem">
            <FormGroup label="Customer" mb="0" controlId="CustomerPriceGridInitialCustomer" flex={1}>
              <SearchSelect
                w="100%"
                dropdownWidth="250px"
                dropdownPlacement="bottom-start"
                selection={resolvedCustomer}
                options={customers ?? []}
                portalize={true}
                placeholder="Select Customer..."
                getLabel={getDisplayNameOrNameLabel}
                onChange={handleCustomerChanged}
                data-testid="customer-price-grid-initial-customer-selector"
              />
            </FormGroup>
          </HStack>
        </VStack>
      ),
    [customers, handleCustomerChanged, isResolvingPersistedCustomer, resolvedCustomer]
  );

  return (
    <VStack h="100%" w="100%" alignItems="flex-start">
      <CustomerPriceGridHeader
        filtersPortalId={filtersPortalId}
        customers={customers ?? []}
        selectedCustomer={resolvedCustomer}
        onCustomerChanged={handleCustomerChanged}
        marketAccounts={availableMarketAccounts}
        selectedMarketAccount={resolvedMarketAccount}
        onMarketAccountChanged={handleMarketAccountChanged}
        filterState={filterState}
        onFilterStateChanged={setFilterState}
        availableProductTypes={availableProductTypes}
      />
      <PriceGridBody>
        <MixpanelSourceProvider value={MixpanelEventSource.CustomerPriceGrid}>
          <PriceGridSettingsContext.Provider value={priceGridSettings}>
            <PriceGrid
              blotterID={blotterID}
              dataObservable={dataObservable}
              columns={columns}
              filtersPortalId={filtersPortalId}
              search={filterState.search}
              onSearchChange={setSearch}
              showInitialState={showInitialState}
              renderInitialState={() => initialState}
            />
          </PriceGridSettingsContext.Provider>
        </MixpanelSourceProvider>
      </PriceGridBody>
    </VStack>
  );
}
